import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../api/client.ts';
import { queryDefaults } from '../api/queries.ts';
import { useSession } from '../auth/session.tsx';
import { LoadFailure } from '../components/LoadFailure.tsx';

/**
 * `/admin/clio-copy` — stage 1 of the Clio import (126; Arthur's rulings Q-S1 to Q-S7, 16 October 2026). Tiers 1-2.
 * Start a copy of every Clio document (or of named matters, by their numbers' digits) into private storage, stop
 * it, and read its counts per matter and its failures. Nothing here opens a copy. Counts and failures are read only
 * on asking, because each read is audited; filenames are the System Administrator's alone (Q-S2).
 */
interface Run {
  id: string;
  scope_all: boolean;
  scope_matters: string[] | null;
  quiet_hours: boolean;
  scan_on_upload: boolean;
  stop_requested: boolean;
  listing_pages: number;
  listing_done: boolean;
  started_at: string;
  state: 'running' | 'stopped' | 'finished';
  ended_at: string | null;
  etag_rechecked: number | null;
  etag_changed: number | null;
}
/** Kept by the database (127), so a finished run and a restarted server still show them. Bigints arrive as strings. */
interface Totals { run_listed: string; run_copied: string; run_copied_bytes: string; listed: string; verified: string; awaiting_scan: string;
  to_copy: string; failed: string; malicious: string; clio_bytes: string; stored_bytes: string }
interface CopyState {
  run: Run | null;
  totals: Totals | null;
  worker: { step: string; waitingFor: string | null; copiedThisProcess: number; failedThisProcess: number; bytesThisProcess: number;
            lastProblem: string | null; clioRequests: number; looping: boolean };
  countsRunning: boolean;
  scanPass: { read: number; answered: number; unanswered: number } | null;
  lastScanPass: { read: number; answered: number; unanswered: number; at: string } | null;
}
interface StorageCheck { clean: { stored: boolean; length: number | null; scan: string | null }; eicar: { stored: boolean; scan: string | null };
  removed: boolean; seconds: number }
interface MatterCounts { matter_digits: string; listed: number; verified: number; copied_awaiting_scan: number; copying: number;
  pending: number; failed: number; malicious: number; clio_bytes: string; stored_bytes: string }
interface Failure { matter_digits: string | null; clio_document_id: string; status: string; attempts: number; last_error: string | null; scan_result: string | null }
interface FailureName { matter_number: string | null; clio_document_id: string; folder_path: string | null; filename: string; status: string; last_error: string | null }

const ERRORS: Record<string, string> = {
  download_refused: 'Clio refused the download', download_failed: 'The download did not complete', size_mismatch: 'Not the size Clio gives',
  block_refused: 'Storage refused a block', store_failed: 'Storage refused the file', length_mismatch: 'Stored length differs',
  scan_unreadable: 'No malware scan answer in a day', gone_from_clio: 'No longer in Clio',
};
const gb = (n: number | string) => `${(Number(n) / 1e9).toFixed(2)} GB`;
const n = (v: number | string) => Number(v).toLocaleString();
const time = (t: string | null) => (t ? new Date(t).toLocaleString() : '');
const button = 'min-h-[44px] rounded-lg px-4 font-semibold disabled:bg-neutral-200 disabled:text-neutral-600';

export function ClioCopy() {
  const { getToken } = useSession();
  const qc = useQueryClient();
  const [all, setAll] = useState(false);
  const [matters, setMatters] = useState('');
  const [quiet, setQuiet] = useState(true);
  const [scan, setScan] = useState(false);
  const [show, setShow] = useState<null | 'report' | 'failures' | 'names'>(null);

  const q = useQuery({
    queryKey: ['clio-copy'],
    queryFn: () => apiFetch<CopyState>('/admin/clio/copy', getToken),
    ...queryDefaults,
    refetchInterval: (query) => (query.state.data?.run?.state === 'running' || query.state.data?.scanPass ? 10_000 : false),
  });
  // Every look at the copy is an audited read, so each is fetched only when its button is pressed, by the path that
  // button names, and is never refetched by itself (no focus, interval or invalidation reaches it).
  const LOOKS = { report: '/admin/clio/copy/report', failures: '/admin/clio/copy/failures', names: '/admin/clio/copy/failure-names' } as const;
  const look = useMutation({
    mutationFn: (which: keyof typeof LOOKS) =>
      apiFetch<{ matters?: MatterCounts[]; failures?: (Failure | FailureName)[] }>(LOOKS[which], getToken),
  });
  const report = { data: look.data, error: look.error };
  const ask = (which: keyof typeof LOOKS) => { setShow(which); look.reset(); look.mutate(which); };
  const start = useMutation({
    mutationFn: () => apiFetch('/admin/clio/copy/start', getToken, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(
      all ? { all: true, quietHours: quiet, scanOnUpload: scan }
          : { matters: matters.split(/[\s,]+/).filter(Boolean), quietHours: quiet, scanOnUpload: scan }) }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['clio-copy'], exact: true }),
  });
  const readScans = useMutation({
    mutationFn: () => apiFetch('/admin/clio/copy/read-scans', getToken, { method: 'POST' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['clio-copy'], exact: true }),
  });
  const check = useMutation({
    mutationFn: (withScan: boolean) => apiFetch<StorageCheck>('/admin/clio/copy/storage-check', getToken,
      { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ scan: withScan }) }),
  });
  const stop = useMutation({
    mutationFn: () => apiFetch('/admin/clio/copy/stop', getToken, { method: 'POST' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['clio-copy'], exact: true }),
  });
  const d = q.data;
  const running = d?.run?.state === 'running';

  return (
    <div className="min-h-dvh bg-page">
      <div className="mx-auto max-w-5xl px-4 py-6">
        <h1 className="title-bar">Clio copy (stage 1)</h1>
        <p className="mt-1 text-neutral-700">
          Copies each Clio document’s latest version, unchanged, into Procura’s private storage, with where it came from. Nothing is
          filed or shown to staff, and nothing in Clio changes. Each file is checked by size and by checksum, and scanned for malware.
        </p>
        {q.error && !d ? (
          <LoadFailure what="The Clio copy" error={q.error} onRetry={() => void q.refetch()} retrying={q.isFetching} forbiddenIsSession={false} />
        ) : !d ? (
          <p className="py-6 text-neutral-600">Loading…</p>
        ) : (
          <>
            <section className="mt-4 rounded-lg border border-line bg-panel p-4">
              <h2 className="text-sm font-bold tracking-[0.08em] text-neutral-600 uppercase">The run</h2>
              {!d.run ? <p className="mt-1 text-neutral-800">No copy has been run.</p> : (
                <dl className="mt-1 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-neutral-900">
                  <dt className="text-neutral-700">State</dt>
                  <dd>{d.run.state === 'running' ? (d.run.stop_requested ? 'Stopping after the current file' : 'Running') : d.run.state === 'stopped' ? 'Stopped' : 'Finished'}
                    {d.run.ended_at ? ` · ${time(d.run.ended_at)}` : ''}</dd>
                  <dt className="text-neutral-700">Scope</dt>
                  <dd>{d.run.scope_all ? 'Every matter' : `Matters ${d.run.scope_matters?.join(', ')}`}{d.run.quiet_hours ? ' · quiet hours only' : ' · at any hour'}
                    {d.run.scan_on_upload ? ' · each file scanned as it lands' : ' · not scanned: files wait for an on-demand scan'}</dd>
                  <dt className="text-neutral-700">Started</dt><dd>{time(d.run.started_at)}</dd>
                  <dt className="text-neutral-700">Clio’s list</dt><dd>{d.run.listing_pages} pages read{d.run.listing_done ? ', complete' : ''}</dd>
                  {running && <><dt className="text-neutral-700">Now</dt>
                    <dd>{d.worker.waitingFor ? `Waiting for ${d.worker.waitingFor}` : d.worker.step}
                      {` · ${d.worker.copiedThisProcess} copied, ${gb(d.worker.bytesThisProcess)}, ${d.worker.failedThisProcess} not, since this server started`}</dd></>}
                  {d.totals && <><dt className="text-neutral-700">This run</dt>
                    <dd>{n(d.totals.run_copied)} copied, {gb(d.totals.run_copied_bytes)} · {n(d.totals.run_listed)} newly listed</dd>
                    <dt className="text-neutral-700">The whole copy</dt>
                    <dd>{n(d.totals.listed)} listed · {n(d.totals.verified)} verified · {n(d.totals.awaiting_scan)} awaiting scan
                      {' · '}{n(d.totals.to_copy)} to copy · {n(d.totals.failed)} failed · {n(d.totals.malicious)} malicious
                      {' · '}{gb(d.totals.clio_bytes)} in Clio, {gb(d.totals.stored_bytes)} stored</dd></>}
                  {d.run.etag_rechecked != null && <><dt className="text-neutral-700">Etags</dt>
                    <dd>{d.run.etag_changed} of {d.run.etag_rechecked} sampled had changed in Clio by the end</dd></>}
                </dl>
              )}
              {d.worker.lastProblem && <p role="alert" className="mt-2 text-warning-fg">Last problem: {ERRORS[d.worker.lastProblem] ?? d.worker.lastProblem}</p>}
              {d.countsRunning && <p className="mt-2 text-neutral-800">A Clio counts run is going: the copy waits for it, as they share Clio’s limit.</p>}
              {running ? (
                <button type="button" disabled={d.run!.stop_requested || stop.isPending} onClick={() => stop.mutate()}
                        className={`${button} mt-3 bg-neutral-900 text-white`}>Stop the copy</button>
              ) : (
                <form className="mt-3 flex flex-wrap items-end gap-3" onSubmit={(e) => { e.preventDefault(); start.mutate(); }}>
                  <label className="flex min-h-[44px] items-center gap-2 text-neutral-900">
                    <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} /> Every matter
                  </label>
                  {!all && (
                    <label className="flex flex-col text-neutral-900">
                      <span className="text-sm text-neutral-700">Matter numbers (digits, separated by commas)</span>
                      <input value={matters} onChange={(e) => setMatters(e.target.value)} inputMode="numeric"
                             className="min-h-[44px] rounded-lg border border-line bg-panel px-3" />
                    </label>
                  )}
                  <label className="flex min-h-[44px] items-center gap-2 text-neutral-900">
                    <input type="checkbox" checked={quiet} onChange={(e) => setQuiet(e.target.checked)} /> Only weeknights 7pm to 7am and weekends
                  </label>
                  <label className="flex min-h-[44px] items-center gap-2 text-neutral-900">
                    <input type="checkbox" checked={scan} onChange={(e) => setScan(e.target.checked)} /> Scan each file as it lands (Defender must be on)
                  </label>
                  <button type="submit" disabled={start.isPending || (!all && !matters.trim())} className={`${button} bg-neutral-900 text-white`}>
                    {d.run?.state === 'stopped' ? 'Start again' : 'Start the copy'}
                  </button>
                </form>
              )}
              {(start.error || stop.error) && <p role="alert" className="mt-2 text-warning-fg">{(start.error ?? stop.error)!.message}</p>}
            </section>

            <section className="mt-4 rounded-lg border border-line bg-panel p-4">
              <h2 className="text-sm font-bold tracking-[0.08em] text-neutral-600 uppercase">Storage and scanning</h2>
              <div className="mt-2 flex flex-wrap gap-3">
                <button type="button" disabled={check.isPending} onClick={() => check.mutate(true)} className={`${button} border border-line bg-panel text-neutral-900`}>
                  Storage check with a scan</button>
                <button type="button" disabled={check.isPending} onClick={() => check.mutate(false)} className={`${button} border border-line bg-panel text-neutral-900`}>
                  Storage check, no scan</button>
                <button type="button" disabled={readScans.isPending || !!d.scanPass} onClick={() => readScans.mutate()} className={`${button} border border-line bg-panel text-neutral-900`}>
                  Read scan results</button>
              </div>
              {check.isPending && <p className="mt-2 text-neutral-800">Checking: up to two and a half minutes with a scan.</p>}
              {check.data && <p className="mt-2 text-neutral-900">
                Clean file {check.data.clean.stored ? `stored (${check.data.clean.length} bytes)` : 'NOT stored'}, scan: {check.data.clean.scan ?? 'no answer'}.
                {check.data.eicar.stored ? ` Test virus stored, scan: ${check.data.eicar.scan ?? 'no answer'}.` : ' No test virus written.'}
                {check.data.removed ? ' Both removed.' : ' NOT all removed.'} {check.data.seconds} s.</p>}
              {(check.error || readScans.error) && <p role="alert" className="mt-2 text-warning-fg">{(check.error ?? readScans.error)!.message}</p>}
              {d.scanPass && <p className="mt-2 text-neutral-800">Reading scan results: {d.scanPass.read} read, {d.scanPass.answered} answered.</p>}
              {d.lastScanPass && !d.scanPass && <p className="mt-2 text-neutral-800">Last read {time(d.lastScanPass.at)}: {d.lastScanPass.answered} answered,
                {' '}{d.lastScanPass.unanswered} with no scan answer yet.</p>}
            </section>

            <section className="mt-4">
              <div className="flex flex-wrap gap-3">
                <button type="button" onClick={() => ask('report')} className={`${button} border border-line bg-panel text-neutral-900`}>Counts per matter</button>
                <button type="button" onClick={() => ask('failures')} className={`${button} border border-line bg-panel text-neutral-900`}>Failures</button>
                <button type="button" onClick={() => ask('names')} className={`${button} border border-line bg-panel text-neutral-900`}>Failures with filenames</button>
              </div>
              <p className="mt-1 text-sm text-neutral-700">Each of these is recorded as a look at the copy. Filenames are for the System Administrator only.</p>
              {show && report.error && <p role="alert" className="mt-2 text-warning-fg">{report.error.message}</p>}
              {show === 'report' && report.data?.matters && (
                <div className="mt-2 overflow-x-auto">
                  <table className="w-full text-left text-neutral-900">
                    <thead><tr className="text-sm text-neutral-700">
                      <th className="py-1 pr-3">Matter</th><th className="pr-3">Listed</th><th className="pr-3">Verified</th><th className="pr-3">Awaiting scan</th>
                      <th className="pr-3">To copy</th><th className="pr-3">Failed</th><th className="pr-3">Malicious</th><th className="pr-3">Clio size</th><th>Stored</th></tr></thead>
                    <tbody>{report.data.matters.map((m) => (
                      <tr key={m.matter_digits} className="border-t border-line">
                        <td className="py-1 pr-3">{m.matter_digits}</td><td className="pr-3">{m.listed}</td><td className="pr-3">{m.verified}</td>
                        <td className="pr-3">{m.copied_awaiting_scan}</td><td className="pr-3">{Number(m.pending) + Number(m.copying)}</td>
                        <td className="pr-3">{m.failed}</td><td className="pr-3">{m.malicious}</td><td className="pr-3">{gb(m.clio_bytes)}</td><td>{gb(m.stored_bytes)}</td>
                      </tr>))}</tbody>
                    {/* The sum of the rows above, added up here: no further read. */}
                    <tfoot>{(() => {
                      const ms = report.data.matters;
                      const sum = (f: (m: MatterCounts) => number | string) => ms.reduce((t, m) => t + Number(f(m)), 0);
                      return (
                        <tr className="border-t-2 border-neutral-900 font-semibold">
                          <th scope="row" className="py-1 pr-3">Total</th><td className="pr-3">{n(sum((m) => m.listed))}</td>
                          <td className="pr-3">{n(sum((m) => m.verified))}</td><td className="pr-3">{n(sum((m) => m.copied_awaiting_scan))}</td>
                          <td className="pr-3">{n(sum((m) => Number(m.pending) + Number(m.copying)))}</td><td className="pr-3">{n(sum((m) => m.failed))}</td>
                          <td className="pr-3">{n(sum((m) => m.malicious))}</td><td className="pr-3">{gb(sum((m) => m.clio_bytes))}</td><td>{gb(sum((m) => m.stored_bytes))}</td>
                        </tr>);
                    })()}</tfoot>
                  </table>
                </div>
              )}
              {(show === 'failures' || show === 'names') && report.data?.failures && (
                report.data.failures.length === 0 ? <p className="mt-2 text-neutral-800">No failures.</p> : (
                  <div className="mt-2 overflow-x-auto">
                    <table className="w-full text-left text-neutral-900">
                      <thead><tr className="text-sm text-neutral-700">
                        <th className="py-1 pr-3">Matter</th><th className="pr-3">Clio document</th>{show === 'names' && <><th className="pr-3">Folder</th><th className="pr-3">Filename</th></>}
                        <th className="pr-3">State</th><th>Why</th></tr></thead>
                      <tbody>{report.data.failures.map((f) => (
                        <tr key={f.clio_document_id} className="border-t border-line">
                          <td className="py-1 pr-3">{'matter_number' in f ? f.matter_number : f.matter_digits}</td><td className="pr-3">{f.clio_document_id}</td>
                          {'filename' in f && <><td className="pr-3">{f.folder_path}</td><td className="pr-3">{f.filename}</td></>}
                          <td className="pr-3">{f.status === 'malicious' ? 'Malicious: kept, never filed' : 'Failed'}</td>
                          <td>{f.last_error ? ERRORS[f.last_error] ?? f.last_error : 'scan_result' in f ? f.scan_result : ''}</td>
                        </tr>))}</tbody>
                    </table>
                  </div>
                )
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}
